/* AdminMobileNav - Responsive Buitn Planten branded navigation for the admin panel. */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, ShoppingBag, ChevronRight, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { getNavGroups } from './AdminNavbar';

interface AdminMobileNavProps {
  adminRole: string;
  onSignOut: () => void;
}

export default function AdminMobileNav({ adminRole, onSignOut }: AdminMobileNavProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const groups = getNavGroups(adminRole);
  const ordersActive = pathname === '/kh-admin/orders';

  const handleNavigate = (href: string) => {
    navigate(href);
    setOpen(false);
  };

  const toggleGroup = (label: string) => {
    setExpandedGroup(prev => prev === label ? null : label);
  };

  return (
    <div className="md:hidden">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="p-2 text-[#1e2b22] hover:bg-[#e7e1d6]/50 hover:text-[#1e2b22]"
      >
        <Menu className="h-5 w-5" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-[#e7e1d6] bg-[#fcfaf6] p-0 text-[#1a1a1a] kh-admin-root">
          <SheetHeader className="border-b border-[#e7e1d6] p-4">
            <SheetTitle className="flex items-center gap-2 text-left">
              <img
                src="/assets/logos/buitnplanten-logo-green.webp"
                alt="Admin"
                className="h-10 w-auto"
              />
              <span className="text-sm font-semibold text-[#5a6552]">Admin</span>
            </SheetTitle>
          </SheetHeader>

          <div className="py-2 overflow-y-auto max-h-[calc(100vh-8rem)]">
            <button
              onClick={() => handleNavigate('/kh-admin')}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                pathname === '/kh-admin' && !ordersActive
                  ? 'text-[#1e2b22] bg-[#e7e1d6]/55 font-medium'
                  : 'text-[#1a1a1a] hover:bg-[#e7e1d6]/50'
              }`}
            >
              Dashboard
            </button>

            <button
              onClick={() => handleNavigate('/kh-admin/orders')}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                ordersActive
                  ? 'text-[#1e2b22] bg-[#e7e1d6]/55 font-medium'
                  : 'text-[#1a1a1a] hover:bg-[#e7e1d6]/50'
              }`}
            >
              <ShoppingBag className="h-4 w-4" />
              Bestellingen
            </button>

            <div className="my-1 mx-4 border-t border-[#e7e1d6]" />

            {groups.map((group) => {
              const Icon = group.icon;
              const isExpanded = expandedGroup === group.label;
              const groupActive = group.matchPaths.some(p => pathname.startsWith(p));

              return (
                <div key={group.label}>
                  <button
                    onClick={() => toggleGroup(group.label)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                      groupActive
                        ? 'text-[#1e2b22] font-medium'
                        : 'text-[#1a1a1a] hover:bg-[#e7e1d6]/50'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="flex-1 text-left">{group.label}</span>
                    <ChevronRight
                      className={`h-4 w-4 opacity-40 transition-transform duration-200 ${
                        isExpanded ? 'rotate-90' : ''
                      }`}
                    />
                  </button>

                  {isExpanded && (
                    <div className="bg-[#e7e1d6]/30">
                      {group.items.map((item) => {
                        const itemActive = pathname === item.href || pathname.startsWith(item.href + '/');
                        return (
                          <button
                            key={item.href}
                            onClick={() => handleNavigate(item.href)}
                            className={`w-full text-left pl-11 pr-4 py-2 text-sm transition-colors ${
                              itemActive
                                ? 'text-[#1e2b22] bg-[#e7e1d6]/55 font-medium'
                                : 'text-[#5a6552] hover:text-[#1e2b22] hover:bg-[#e7e1d6]/50'
                            }`}
                          >
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="my-1 mx-4 border-t border-[#e7e1d6]" />

            <button
              onClick={() => { onSignOut(); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Uitloggen
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
